import clsx from 'clsx';
import { ImageProps as NextImageProps } from 'next/image';
import React, { useMemo } from 'react';
import { mq } from '~/lib';
import { useCanHover } from '~/shared/hooks/useCanHover';
import { DynamicImageLoader } from '~/shared/components/DynamicImageLoader';
import { ProductImage } from '../../../ProductCard/ProductCard.definition';
import styles from './ProductCardMedia.module.scss';

interface ProductCardMediaProps extends React.HTMLAttributes<HTMLDivElement> {
    name: string;
    sources: ProductImage[];
    imagePriority?: boolean;
    imageLoading?: NextImageProps['loading'];
}

export const ProductCardMedia: React.FC<ProductCardMediaProps> = ({
    className,
    sources,
    imageLoading,
    imagePriority,
    name,
    ...rest
}) => {
    const primaryImage = sources[0];
    const secondaryImage = sources[1];

    const canHover = useCanHover();

    // We do not want to load in the second image on (e.g., devices that cannot utilize hover, etc.)
    const shouldShowHoverImage = !!secondaryImage && canHover;

    const sharedProps = useMemo(
        () => ({
            fill: true,
            title: name,
            sizes: `${mq(0, 'md', false)} 50vw, ${mq(0, 'lg', false)} 34vw, 25vw`,
        }),
        [],
    );

    return (
        <div
            className={clsx(styles.cardMedia, className, {
                [styles.isHoverable]: shouldShowHoverImage,
            })}
            {...rest}
        >
            <div className={styles.packshot}>
                <DynamicImageLoader
                    {...sharedProps}
                    className={clsx(styles.image, styles.primaryImage)}
                    priority={imagePriority}
                    loading={imageLoading}
                    src={primaryImage.src}
                    alt={primaryImage.alt ?? name}
                />
            </div>

            {shouldShowHoverImage && (
                <DynamicImageLoader
                    {...sharedProps}
                    className={clsx(styles.image, styles.secondaryImage)}
                    loading="lazy"
                    src={secondaryImage.src}
                    alt={secondaryImage.alt ?? name}
                />
            )}
        </div>
    );
};

export default ProductCardMedia;
